import { escapeRegExp } from './escapeRegExp';
import { Localization } from '../Localization';

export type TranslateOptions = {
  [index: string]: string | number | undefined | null;
};
type Cache = { [index: string]: string | number };

const memo: Cache = {};

export type Translate<TLocalization extends Localization> = (
  key: string,
  options?: TranslateOptions | null | undefined,
  messages?: TLocalization,
) => string;

export function getOptionsKey(
  key: string,
  options: TranslateOptions | null | undefined,
) {
  return `${key}${JSON.stringify(options)}`;
}

export function getMemoized(
  key: string,
  options: TranslateOptions | null | undefined,
): string | undefined {
  if (memo[key]) {
    return String(memo[key]);
  }

  let optionsKey = '';

  if (options) {
    optionsKey = getOptionsKey(key, options);

    if (memo[optionsKey]) {
      return String(memo[optionsKey]);
    }
  }
}

export function translateMessage(
  key: string,
  message: string,
  options?: TranslateOptions | null,
): string {
  if (options == null) {
    memo[key] = message;
    return message;
  }

  const objectKeys = Object.keys(options);
  const messageWithOptions = objectKeys.reduce((accumulator, currentValue) => {
    const optionValue = String(options[currentValue] || '');
    const prefix =
      typeof optionValue === 'number' ? `=${optionValue}` : optionValue;
    const PHRAZE_REGEXP = new RegExp(
      `${escapeRegExp(prefix)} \\[([^[\\]]+(?=]))\\]`,
    );

    const PHRAZE_FALLBACK_REGEXP = new RegExp('other \\[([^[\\]]+(?=]))\\]');
    let phraze = '';
    const SANITIZE_REGEXP = new RegExp(`{${currentValue}}`, 'g');
    const sanitizedMessage = accumulator.replace(SANITIZE_REGEXP, optionValue);

    if (sanitizedMessage.match(PHRAZE_REGEXP)) {
      phraze = sanitizedMessage.match(PHRAZE_REGEXP)![1];
    } else if (sanitizedMessage.match(PHRAZE_FALLBACK_REGEXP)) {
      phraze = sanitizedMessage.match(PHRAZE_FALLBACK_REGEXP)![1];
    }

    const phrazeWithNumber = phraze.replace(/#/, String(options[currentValue]));
    const PLURALIZED_MESSAGE_REGEXP = new RegExp(
      `{${currentValue}, plural, .*?}`,
    );
    const pluralizedMessage = sanitizedMessage.replace(
      PLURALIZED_MESSAGE_REGEXP,
      phrazeWithNumber,
    );

    return pluralizedMessage;
  }, message);

  const optionsKey = getOptionsKey(key, options);
  memo[optionsKey] = messageWithOptions;

  return messageWithOptions;
}

export function translate(
  key: string,
  options: TranslateOptions | null | undefined,
  messages: Object,
): string {
  const memoized = getMemoized(key, options);
  if (memoized !== undefined) return memoized;

  const path: string = key.replace(/\[(\w+)\]/g, '.$1');
  const sanitizedPath: string = path.replace(/^\./, '');
  const arr: any[] = sanitizedPath.split('.');

  try {
    const message = arr.reduce(
      (accumulator, currentValue) => accumulator[currentValue],
      messages,
    );

    if (message) {
      return translateMessage(key, message, options);
    }

    memo[key] = `{${sanitizedPath}}`;

    return `{${sanitizedPath}}`;
  } catch (exception) {
    memo[key] = `{${sanitizedPath}}`;

    return `{${sanitizedPath}}`;
  }
}

export function createTranslate<TLocalization extends Localization>(
  localization: TLocalization,
): Translate<TLocalization> {
  return (key, options, customLocalization) => {
    return translate(key, options, customLocalization || localization);
  };
}
